import { z } from "zod";
import { mdfRequestCreateSchema } from "../domain";

// Bucket 9 — portal MDF request submission (drafts, cancel, resubmit,
// multi-file attachments, partner-side comments). The legacy
// `mdfRequestCreateSchema` already lives in `/types/domain.ts` (re-exported by
// @colab/shared) and is reused here for the submit step. Only the new
// route-body schemas land in this file, per the Phase 6 architectural note
// in MIGRATION.md.

// ---------------------------------------------------------------------------
// Local Zod helpers. Unlike types/domain.ts's optionalText (which uses a
// .transform to coerce undefined/"" → null), the bucket-9 schemas use plain
// nullable+optional so input and output types match — react-hook-form's
// zodResolver chokes on input/output divergence in field-level resolvers.
// The route handler is responsible for null-vs-empty normalisation; the
// service-layer patchDraft treats `null` and `""` identically.
// ---------------------------------------------------------------------------
const optionalText = z.string().trim().max(2000).nullable().optional();

// ---------------------------------------------------------------------------
// Draft lifecycle — initial create needs only an allocation pick; patch is
// a partial body the auto-save hook posts on every debounced change.
// ---------------------------------------------------------------------------
export const mdfDraftCreateSchema = z.object({
  allocation_id: z.string().uuid(),
});
export type MdfDraftCreateInput = z.infer<typeof mdfDraftCreateSchema>;

export const mdfDraftPatchSchema = z.object({
  // amount_eur can be NULL on draft rows; 0 is allowed during editing but
  // the partial CHECK constraint enforces > 0 on promote.
  amount_eur: z
    .number()
    .int("Must be a whole number of EUR")
    .min(0)
    .nullable()
    .optional(),
  campaign_name: z.string().trim().max(200).nullable().optional(),
  campaign_type: optionalText,
  description: optionalText,
});
// Input and output match because optionalText has no .transform — that
// keeps zodResolver happy on the SPA side without a separate route-only
// type. The service in apps/api treats null and undefined interchangeably.
export type MdfDraftPatchInput = z.infer<typeof mdfDraftPatchSchema>;

// Submit (promote draft → pending). Reuses the legacy create-time validation
// for amount/name/etc., but swaps the legacy single-URL field for a list of
// attachment ids (rows already inserted by the upload-finalize step).
export const mdfDraftSubmitSchema = mdfRequestCreateSchema
  .omit({ supporting_doc_url: true })
  .extend({
    attachment_ids: z
      .array(z.string().uuid())
      .max(10, "At most 10 attachments per request")
      .default([]),
  });
export type MdfDraftSubmitInput = z.output<typeof mdfDraftSubmitSchema>;

// Cancel / resubmit — id is on the URL, no body. Schemas exist mostly for
// completeness + type inference on the hook side.
export const mdfRequestCancelSchema = z.object({ id: z.string().uuid() });
export type MdfRequestCancelInput = z.infer<typeof mdfRequestCancelSchema>;

export const mdfRequestResubmitSchema = z.object({ id: z.string().uuid() });
export type MdfRequestResubmitInput = z.infer<typeof mdfRequestResubmitSchema>;

// ---------------------------------------------------------------------------
// Attachments — three-step upload (init → direct-PUT → finalize) mirrors the
// bucket-4 content-asset upload pattern. MIME validation lives in the service
// layer; the schema is structural only.
// ---------------------------------------------------------------------------
const ATTACHMENT_MAX_BYTES = 26_214_400; // 25 MB

export const mdfAttachmentUploadInitSchema = z.object({
  file_name: z.string().trim().min(1).max(255),
  content_type: z.string().min(1).max(120),
  size_bytes: z.number().int().min(1).max(ATTACHMENT_MAX_BYTES),
});
export type MdfAttachmentUploadInitInput = z.infer<
  typeof mdfAttachmentUploadInitSchema
>;

export const mdfAttachmentFinalizeSchema = z.object({
  request_id: z.string().uuid(),
  storage_key: z.string().min(1).max(512),
  file_name: z.string().trim().min(1).max(255),
  content_type: z.string().min(1).max(120),
  size_bytes: z.number().int().min(1).max(ATTACHMENT_MAX_BYTES),
});
export type MdfAttachmentFinalizeInput = z.infer<
  typeof mdfAttachmentFinalizeSchema
>;

// ---------------------------------------------------------------------------
// Partner-side comment add. Body length matches the DB CHECK
// (mdf_request_comments_body_check: 1..4000 chars after btrim).
// ---------------------------------------------------------------------------
export const mdfCommentCreateSchema = z.object({
  body: z.string().trim().min(1).max(4000),
});
export type MdfCommentCreateInput = z.infer<typeof mdfCommentCreateSchema>;

// ---------------------------------------------------------------------------
// Bucket 10 — admin decline body. The service layer enforces the same
// length cap internally, but parsing at the route boundary keeps 4xx vs 5xx
// cleanly separated (invalid body returns 400 with field-level message; the
// service's own check would surface a generic 400 via HTTPException).
// ---------------------------------------------------------------------------
export const mdfDeclineRequestSchema = z.object({
  reason: z.string().trim().min(1, "Decline reason is required").max(1000),
});
export type MdfDeclineRequestInput = z.infer<typeof mdfDeclineRequestSchema>;

// ---------------------------------------------------------------------------
// MIME allowlist for MDF attachments. Enforced in the service layer
// (apps/api/src/services/mdf.ts) on the upload-init handler. Mirrors the
// legacy /app/portal/mdf/_actions.ts:74-83 list plus xlsx for budget sheets.
// ---------------------------------------------------------------------------
export const MDF_ATTACHMENT_MIME_ALLOWLIST = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;
export type MdfAttachmentMime = (typeof MDF_ATTACHMENT_MIME_ALLOWLIST)[number];

export const MDF_ATTACHMENT_MAX_BYTES = ATTACHMENT_MAX_BYTES;
